/* eslint-disable import/no-unresolved */
import axios from 'axios';
import logger from 'logger/logHelper';
import {
  getAccessToken,
  getAxiosError,
  updateAccessTokenInCache
} from '../utils';
import LoginApi from './login.service';
import { BASE_URL } from '../constants';
import _ from 'underscore';
import history from './history.service';

const CancelTokenSource = axios.CancelToken.source();

class NetworkService {
  /**
   *
   * @param {String} url
   * @param {Object} body
   * @param {Object} params
   */
  static post(url, body, params) {
    if (navigator.onLine) {
      logger.logStartOfNetworkRequest({ url, body });
      const headers = getHeaders();
      const _axios = NetworkService.getAxiosInstance(headers);
      return _axios
        .post(url, body, { params })
        .then(function (data) {
          logger.logAPISuccess({ data, url, body, headers });
          return Promise.resolve(data);
        })
        .catch(function (error) {
          logger.logAPIError({ error, url, body, headers });
          const customError = {
            error,
            isErrorFromApi: true
          };
          return Promise.reject(customError);
        });
    }
    // TO:DO add a offline network layer.
    return Promise.reject('You are offline.');
  }

  /**
   *
   * @param {String} url
   * @param {Object} params
   */
  static get(url, params) {
    if (navigator.onLine) {
      logger.logStartOfNetworkRequest({ url, params });

      const headers = getHeaders();
      const _axios = NetworkService.getAxiosInstance(headers);
      return _axios
        .get(url, params)
        .then(function (data) {
          logger.logAPISuccess({
            data,
            url,
            params,
            headers,
            requestType: 'GET'
          });
          return Promise.resolve(data);
        })
        .catch(function (error) {
          logger.logAPIError({
            error,
            url,
            params,
            headers,
            requestType: 'GET'
          });
          const customError = {
            error,
            isErrorFromApi: true
          };
          return Promise.reject(customError);
        });
    }
    return Promise.reject('You are offline');
  }

  static put(url, params) {
    if (navigator.onLine) {
      const headers = getHeaders();
      const _axios = NetworkService.getAxiosInstance(headers);
      return _axios
        .put(url, params)
        .then(function (data) {
          logger.logAPISuccess({
            data,
            url,
            params,
            headers,
            requestType: 'PUT'
          });
          return Promise.resolve(data);
        })
        .catch(function (error) {
          logger.logAPIError({
            error,
            url,
            params,
            headers,
            requestType: 'PUT'
          });
          const customError = {
            error,
            isErrorFromApi: true
          };
          return Promise.reject(customError);
        });
    }
    return Promise.reject('You are offline');
  }

  static async patch(url, params) {
    if (!navigator.onLine) {
      return Promise.reject('You are offline');
    }

    const headers = getHeaders();
    const _axios = NetworkService.getAxiosInstance(headers);

    try {
      const data = await _axios.patch(url, params);
      logger.logAPISuccess({
        data,
        url,
        params,
        headers,
        requestType: 'PATCH'
      });
      return data;
    } catch (error) {
      logger.logAPIError({
        error,
        url,
        params,
        headers,
        requestType: 'PATCH'
      });
      const customError = {
        error,
        isErrorFromApi: true
      };
      return Promise.reject(customError);
    }
  }

  static async delete(url, config = {}) {
    if (!navigator.onLine) {
      return Promise.reject('You are offline');
    }

    logger.logStartOfNetworkRequest({ url, params });

    const { params, payload } = config;
    const headers = getHeaders();
    const _axios = NetworkService.getAxiosInstance(headers);

    try {
      const data = await _axios.delete(url, { params, data: payload });
      logger.logAPISuccess({
        data,
        url,
        params,
        headers,
        requestType: 'DELETE'
      });
      return data;
    } catch (error) {
      logger.logAPIError({
        error,
        url,
        params,
        headers,
        requestType: 'DELETE'
      });
      const customError = {
        error,
        isErrorFromApi: true
      };
      return Promise.reject(customError);
    }
  }

  static getAxiosInstance(headers) {
    const instance = axios.create({
      baseURL: BASE_URL,
      headers
    });
    instance.interceptors.response.use(
      function (response) {
        if (response.status === 'success' || response.status === 200) {
          const { data } = response;
          if (data && data.requestId) {
            updateAccessTokenInCache(data.requestId);
          }
          const { result } = data;
          return result || data;
        }
        return response;
      },
      function (error) {
        let errCode;
        if (error && error.response) {
          const { response } = error;
          if (response.data && response.data.requestId) {
            updateAccessTokenInCache(response.data.requestId);
          }
          if (!_.isEmpty(response.data)) {
            const data = response.data;
            if (data.exception && data.exception.error) {
              const errObj = data.exception.error;
              errCode = errObj.code;
            }
          }
          else {
            errCode = response.status;
          }
          if (errCode === 401) {
            LoginApi.clearAndRedirectToLogin();
          }
          else if (errCode === "E301") {
            history.push('redirect');
            return true;
          }
          throw getAxiosError(error);
        } else if (error.request) {
          logger.logInfo('Offline!');
          throw error.request;
        } else {
          throw error;
        }
      }
    );
    return instance;
  }

  static async ping(url) {
    url = url || `/ping`;
    const begin = new Date().getTime();
    await NetworkService.get(`${BASE_URL}${url}`);
    const end = new Date().getTime();
    return (end - begin) / 1000;
  }

  static async isHighLatency(url) {
    const THRESHOLD_IN_SEC = 10;
    const timeTakenPing = await NetworkService.ping(url);
    return timeTakenPing > THRESHOLD_IN_SEC;
  }
}

const getHeaders = () => {
  let headers = {};
  try {
    const accessToken = getAccessToken();
    // let refreshToken = getRefreshToken();
    const companyId = JSON.parse(localStorage.getItem('company-id'));
    const userId = JSON.parse(localStorage.getItem('user-id'));
    let timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timezoneOffset = new Date().getTimezoneOffset();
    headers = {
      'access-token': accessToken,
      // "refresh-token": refreshToken,
      'company-id': companyId,
      'user-id': userId,
      'Content-Type': 'application/json',
      'request-from': 'ANDROID',
      timezoneName,
      'timezone-offset': timezoneOffset
    };
  } catch (e) {
    logger.logError(e);
  }
  return headers;
};

export { NetworkService, CancelTokenSource };
